import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import { GOLD } from "@/lib/brand";

const GoogleButton = () => {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={handleGoogle} data-testid="google-login-btn" className="w-full rounded-full border border-white/15 bg-white/5 text-[#F5F1E6] font-semibold py-3 hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
      <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.3l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 6.8-4.9 6.8-15 0-1.2-.1-2.3-.4-3.5z"/></svg>
      Continua con Google
    </button>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success("Accesso effettuato");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-xl border border-white/15 bg-white/5 text-[#F5F1E6] px-4 py-3 placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#B2A475]/40 focus:border-[#B2A475] transition";

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center px-4 py-10 relative overflow-hidden" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: GOLD }} />
      <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: GOLD }} />
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <BrandLogo className="w-12 h-12" />
          <span className="text-[#F5F1E6] text-2xl tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The Cube Design</span>
        </Link>
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 shadow-2xl">
          <h1 className="text-3xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Area Ristoratori</h1>
          <p className="text-white/50 text-sm mt-1">Accedi per gestire il tuo menù.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-white/70">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-white/70">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className={inputCls} />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full font-semibold py-3 transition-transform hover:scale-[1.01] flex items-center justify-center gap-2" style={{ background: GOLD, color: "#0B0B0C" }}>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Accedi
            </button>
            <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer select-none pt-1" data-testid="remember-me-row">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-me" className="w-4 h-4 accent-[#B2A475]" />
              Ricordami su questo dispositivo
            </label>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs text-white/40"><div className="flex-1 h-px bg-white/10" /> oppure <div className="flex-1 h-px bg-white/10" /></div>
          <GoogleButton />
          <p className="mt-6 text-center text-sm text-white/50">
            Non hai un account? <Link to="/register" className="font-semibold gold-text" data-testid="go-register">Registrati</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
